import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {CurveNode} from "#template/nodes/object/curve/curve-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MeshLikeNode} from "#template/nodes/types"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import type {CurveParametersCommon, PositionControlPoint, UVControlPoint} from "#template/nodes/object/curve/types"
import {curveParametersCommon, positionControlPoint, uvControlPoint, isUVControlPoint} from "#template/nodes/object/curve/validation"
import type {SceneInteractiveNode} from "#template/role-markers"
import {isProceduralTemplateNode} from "#template/role-markers"
import type {INodeSceneInteractionsContext, TemplateNodePart, TransformInteraction} from "#template/interactions"
import {getControlPointPartNumber, isControlPointPart} from "#template/interactions"
import {Matrix4, Vector2, Vector3} from "@cm/math"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {ControlPoint, CurveData} from "#template/interfaces/object-data"
import type {CompleteCurveData, ReifiedCurveData} from "#template/interfaces/curve-data"
import {SampleCurve, GetControlPoints} from "#template/runtime-graph/nodes/sample-curve"
import {LiveControlPointsValue, LiveControlPointsValueState} from "#template/runtime-graph/nodes/live-control-points-value"
import type {LiveValueAccessorListEntry} from "#template/runtime-graph/nodes/compile-template"
import {hashObject} from "@cm/utils"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {SceneNodes} from "#template/interfaces/scene-object"
import type {IMaterialData} from "@cm/material-nodes"
import {getRootTemplateNodeForObjectPath} from "#template/object-path-utils"
import {visitNone} from "#template/template-node/visit-helpers"

export function completeCurveDataForControlPointsDefinedCurve(
    scope: GraphBuilderScope,
    controlPoints: BuilderInlet<ControlPoint[]>,
    closed: BuilderInlet<boolean>,
    segmentLength: BuilderInlet<number> = 0.1,
    allowScaling: BuilderInlet<boolean> = true,
    offsetLength: BuilderInlet<number | undefined> = undefined,
) {
    const {curvePoints} = scope.node(SampleCurve, {
        closed: scope.pureLambda(closed, (closed) => (closed ? "repeat" : false), "closed"),
        allowScaling,
        controlPoints,
        segmentLength,
        offsetLength,
    })

    const reifiedCurveDataContentHash = scope.pureLambda(
        scope.tuple(controlPoints, closed, segmentLength, allowScaling, offsetLength),
        ([controlPoints, closed, segmentLength, allowScaling, offsetLength]) => hashObject({controlPoints, closed, segmentLength, allowScaling, offsetLength}),
        "reifiedCurveDataContentHash",
    )

    return scope.pureLambda<[Omit<ReifiedCurveData, "contentHash"> | null, string], CompleteCurveData>(
        scope.tuple(curvePoints, reifiedCurveDataContentHash),
        ([curvePoints, reifiedCurveDataContentHash]) => ({
            reified: curvePoints ? {...curvePoints, contentHash: reifiedCurveDataContentHash} : null,
            abstract: undefined,
        }),
        "completeCurveData",
    )
}

export type MeshCurveParameters = {
    name: string
    mesh: MeshLikeNode | null
    controlPoints: PositionControlPoint[] | UVControlPoint[]
    uvChannel?: 0 | 1
} & CurveParametersCommon &
    ObjectNodeParameters

const meshCurveDefinition: ObjectNodeDefinition<MeshCurveParameters> = {
    nodeClass: "MeshCurve",
    validation: {
        parameters: z.extend(
            z.extend(
                z.object({
                    name: z.string(),
                    mesh: z.nullable(meshLike),
                    controlPoints: z.union([z.array(positionControlPoint), z.array(uvControlPoint)]),
                    uvChannel: z.optional(z.union([z.literal(0), z.literal(1)])),
                }),
                curveParametersCommon.shape,
            ),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MeshCurve extends CurveNode<MeshCurveParameters> implements SceneInteractiveNode {
    public override get definition() {
        return meshCurveDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MeshCurveParameters> {
        if (this.parameters.mesh === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MeshCurveParameters> {
        const {mesh, closed, controlPoints: controlPointsData, resolution, uvChannel} = this.parameters

        if (!mesh) return skipped

        const {evaluator} = context
        const {templateScope} = evaluator
        const {templateContext} = evaluator
        const {sceneManager} = templateContext

        const scope = evaluator.getScope(this)

        const [meshObjectData, meshObjectDataInvalid] = scope.branch(
            scope.pureLambda(
                evaluator.evaluate(mesh),
                (meshObjectData) => {
                    if (!meshObjectData) return null
                    return {...meshObjectData, visible: true}
                },
                "meshObjectData",
            ),
        )

        const preDisplayList = scope.pureLambda(
            meshObjectData,
            (meshObjectData) => meshObjectData.preDisplayList.filter((x) => SceneNodes.DataObjectReference.is(x) && x.origin.type === "mesh"),
            "filteredPreDisplayList",
        )

        const transform = scope.get(meshObjectData, "matrix")

        const reifiedMeshData = scope.get(scope.get(meshObjectData, "completeMeshData"), "reified")

        const liveControlPointsNode = scope.node(LiveControlPointsValue, {
            state: scope.state(LiveControlPointsValueState, "controlPointState"),
            value: controlPointsData,
        })

        context.currentTemplate.liveValueAccessorList.push(
            scope.struct<LiveValueAccessorListEntry>("LiveValueAccessorListEntry", {
                templateNode: this,
                accessor: liveControlPointsNode.accessor,
            }),
        )

        const {controlPoints} = scope.node(GetControlPoints, {
            sceneManager,
            controlPointsData: liveControlPointsNode.output,
            reifiedMeshData,
            uvChannel: uvChannel ?? null,
        })

        const completeCurveData = completeCurveDataForControlPointsDefinedCurve(scope, controlPoints, closed, resolution)

        const curveData = scope.struct<CurveData>("CurveData", {
            type: "curve",
            visible: scope.pureLambda(
                scope.tuple(scope.get(meshObjectData, "visible"), this.parameters.visible),
                ([meshVisible, curveVisible]) => meshVisible && curveVisible,
                "curveVisible",
            ),
            controlPoints,
            closed,
            completeCurveData,
            matrix: transform,
        })

        templateScope.alias(scope.phi(curveData, meshObjectDataInvalid), `curveData-${evaluator.getLocalId(this)}`)

        this.setupObject(
            scope,
            context,
            "MeshCurveControl",
            undefined,
            meshObjectData,
            (objectProps) => {
                return {
                    displayList: scope.pureLambda(
                        scope.tuple(
                            objectProps.$id,
                            objectProps.id,
                            objectProps.transform,
                            objectProps.path,
                            controlPoints,
                            closed,
                            meshObjectData,
                            completeCurveData,
                        ),
                        ([$id, id, transform, path, controlPoints, closed, meshObjectData, completeCurveData]) => {
                            const mesh: SceneNodes.Mesh = {
                                type: "Mesh",
                                id: meshObjectData.id,
                                path: meshObjectData.path,
                                transform: meshObjectData.matrix,
                                completeMeshData: meshObjectData.completeMeshData,
                                meshRenderSettings: {},
                                materialMap: new Map<number, IMaterialData | null>(),
                                visibleDirectly: false,
                                visibleInReflections: false,
                                visibleInRefractions: false,
                                receiveRealtimeShadows: false,
                                castRealtimeShadows: false,
                                isDecal: false,
                                parent: null,
                                isProcedural: meshObjectData.isProcedural,
                            }

                            const meshCurveControl: SceneNodes.MeshCurveControl & {$id: string} = {
                                type: "MeshCurveControl",
                                $id,
                                id,
                                path,
                                transform,
                                controlPoints,
                                closed,
                                mesh,
                                completeCurveData,
                                selectable: path.length === 1 || isProceduralTemplateNode(getRootTemplateNodeForObjectPath(path)),
                            }
                            return meshCurveControl
                        },
                        "meshCurveControl",
                    ),
                    preDisplayList: scope.pureLambda(
                        scope.tuple(preDisplayList, objectProps.path, scope),
                        ([preDisplayList, path, scope]) => {
                            return preDisplayList.map((x) => {
                                if (SceneNodes.DataObjectReference.is(x)) {
                                    const finalId = x.id.split("@").pop()
                                    if (!finalId) throw new Error("Invalid DataObjectReference id")
                                    return {...x, id: `${scope.getPrefix()}:DataObjectReference@${finalId}`, path}
                                }
                                return x
                            })
                        },
                        "mappedPreDisplayList",
                    ),
                }
            },
            meshObjectDataInvalid,
        )

        return visitNone(this.parameters)
    }

    override canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    override canTransformPart(part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): boolean {
        return part !== "root"
    }

    private controlPointsForDrag(
        transform: Matrix4,
        part: TemplateNodePart["part"],
        context: INodeSceneInteractionsContext,
    ): MeshCurveParameters["controlPoints"] | undefined {
        if (!isControlPointPart(part)) return undefined

        const sceneNodeMatrix = context.getTransformAccessor(this)?.getTransform()
        if (!sceneNodeMatrix) return undefined

        const translationWorld = transform.getTranslation()
        const translationCamera = context.camera.worldInverseMatrix.multiplyVectorXYZW(translationWorld.x, translationWorld.y, translationWorld.z, 1)
        const projection = context.camera.projectionMatrix.multiplyVectorXYZW(translationCamera[0], translationCamera[1], translationCamera[2], 1)

        const pointX = ((projection[0] / projection[3]) * 0.5 + 0.5) * context.canvasRect.width
        const pointY = ((-projection[1] / projection[3]) * 0.5 + 0.5) * context.canvasRect.height

        const sceneNodeParts = context.getSceneNodes(this).filter(SceneNodes.MeshCurveControl.is)
        if (sceneNodeParts.length !== 1) return undefined
        const meshCurveControl = sceneNodeParts[0]

        const meshIntersection = context
            .getSceneNodePartsUnderCursor(pointX, pointY)
            .find(({sceneNodePart}) => sceneNodePart.sceneNode.id === meshCurveControl.mesh.id)

        if (!meshIntersection) return undefined
        const {intersection} = meshIntersection
        if (!intersection) return undefined

        const controlPoints = [...this.parameters.controlPoints] as typeof this.parameters.controlPoints
        const controlPoint = controlPoints[getControlPointPartNumber(part)]
        if (isUVControlPoint(controlPoint)) {
            const uvChannel = this.parameters.uvChannel
            if (uvChannel === undefined) throw Error("UV channel is undefined")
            const surfaceUV = uvChannel === 0 ? intersection.uv : intersection.uv1
            if (!surfaceUV) return undefined
            controlPoint.uv = [surfaceUV.x, surfaceUV.y]
        } else {
            const positionControlPoint = controlPoint
            const {point, normal} = intersection
            if (!normal) return undefined
            const surfacePoint = Vector3.fromArray(sceneNodeMatrix.inverse().multiplyVectorXYZW(point.x, point.y, point.z, 1.0))
            const surfaceNormal = new Vector3(normal.x, normal.y, normal.z).normalized()
            positionControlPoint.position = surfacePoint.toArray()
            positionControlPoint.normal = surfaceNormal.toArray()
        }
        return controlPoints
    }

    override beginTransformInteraction(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): TransformInteraction | undefined {
        if (!isControlPointPart(part)) return undefined
        const accessor = context.getLiveValueAccessor<MeshCurveParameters["controlPoints"]>(this)
        if (!accessor) return undefined
        let lastControlPoints: MeshCurveParameters["controlPoints"] | undefined
        return {
            update: (transform) => {
                const controlPoints = this.controlPointsForDrag(transform, part, context)
                if (!controlPoints) return
                lastControlPoints = controlPoints
                accessor.setLiveValue(controlPoints)
            },
            commit: () => {
                accessor.endInteraction()
                if (lastControlPoints) this.updateParameters({controlPoints: lastControlPoints})
            },
            cancel: () => accessor.endInteraction(),
        }
    }

    override getPartTransform(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): Matrix4 {
        if (!isControlPointPart(part)) return Matrix4.identity()
        const parameters = this.parameters

        const controlPoint = parameters.controlPoints.at(getControlPointPartNumber(part))
        if (!controlPoint) return Matrix4.identity()

        const matrix = context.getTransformAccessor(this)?.getTransform() ?? Matrix4.identity()

        const controlPointPosition = (() => {
            if (isUVControlPoint(controlPoint)) {
                const mesh = parameters.mesh
                if (!mesh) throw new Error("Mesh parameter of MeshCurve is not set")
                const sceneMeshNodes = context.getSceneNodes(mesh)
                if (sceneMeshNodes.length !== 1 || !SceneNodes.Mesh.is(sceneMeshNodes[0])) {
                    throw new Error(`Exactly one scene mesh part for mesh parameter on MeshCurve node expected, found: ${sceneMeshNodes.length})`)
                }
                const sceneMesh = sceneMeshNodes[0]
                const uvChannel = parameters.uvChannel
                if (uvChannel === undefined) throw new Error("uvChannel parameter of MeshCurve not set but control point is in UV space")
                const uvs = sceneMesh.completeMeshData.reified.uvs
                if (uvs.length <= uvChannel) throw new Error(`UV channel ${uvChannel} not found in mesh`)
                const intersection = context.meshGeometryQueries.projectPointToMeshInUVSpace(
                    sceneMesh.completeMeshData.reified,
                    uvChannel,
                    Vector2.fromArray(controlPoint.uv),
                )
                if (!intersection) throw new Error("Failed to convert UV control point to Position control point")
                return intersection.position.toArray()
            } else return controlPoint.position
        })()

        const transformedPoint = matrix.multiplyVectorXYZW(controlPointPosition[0], controlPointPosition[1], controlPointPosition[2], 1.0)
        return Matrix4.translation(transformedPoint[0], transformedPoint[1], transformedPoint[2])
    }
}
